import React from 'react';
import { PageHero, BottomCTA } from '../components/PageHelpers';
import { Sec, SH } from '../components/Section';
import { Crd } from '../components/Button';
import { BulletList } from '../components/AnimatedComponents';
import { MockupGraphic } from '../components/MockupGraphic';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { C } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';

// For Founders Page - Exact original content
export const ForFounders = () => {
  const { theme } = useTheme();

  const items = [
    'DecX Score — know where you stand in 24 hours',
    'Investor Insights — what they need to see, and when',
    'Industry Benchmarks — how your deck compares',
    'Pitch Canvas — structure your winning narrative',
    'Outreach Sequences — emails that get meetings',
    "Strategy Calls — 30 mins with operators who've raised"
  ];

  const testimonials = [
    { quote: 'DecX showed me I was burying traction on slide 9. Fixed it. Closed in 3 weeks.', name: 'Alex K', role: 'Seed Founder' },
    { quote: 'The outreach sequences alone were worth it. Got 12 meetings in 2 weeks.', name: 'Priya S', role: 'Series A' },
    { quote: 'Finally knew exactly what investors wanted to see. Raised $2M in 6 weeks.', name: 'James T', role: 'Pre-seed' },
    { quote: 'The pitch canvas transformed how I tell our story. Got 3 term sheets.', name: 'Maria L', role: 'Founder' },
    { quote: 'Industry benchmarks showed us we were underselling. Doubled our ask.', name: 'David C', role: 'B2B SaaS' },
    { quote: 'Strategy call was invaluable. Neeraj helped me nail our narrative.', name: 'Sarah W', role: 'FinTech' }
  ];

  return (
    <div data-testid="for-founders-page">
      <PageHero
        eyebrow="FOR FOUNDERS"
        title="For Founders Who Are"
        accent="Done Guessing"
        subtitle="You've built something worth funding. Your deck should tell that story."
        cta="Get Free Score"
        ctaLink="/score"
        twoColumn
      >
        <MockupGraphic type="score" label="Pitch Intelligence" />
      </PageHero>

      <Sec dark={theme === 'light'} style={{ paddingTop: '40px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <SH
            eyebrow="WHAT YOU GET"
            title="Everything to"
            accent="Close Your Round"
            center
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginTop: '32px' }}
          >
            <Crd glow>
              <BulletList items={items} />
            </Crd>
          </motion.div>
        </div>
      </Sec>

      <Sec dark={theme === 'light'} style={{ paddingTop: '0', overflow: 'hidden' }}>
        <TestimonialsSection
          eyebrow="TESTIMONIALS"
          title="Founders Who"
          accent="Closed"
          testimonials={testimonials}
        />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginTop: '48px' }}
        >
          <p style={{ fontSize: '18px', fontWeight: 600, color: C.secondary }}>
            Join founders who've raised $500M+.
          </p>
        </motion.div>
      </Sec>

      <BottomCTA
        title="Ready to Close Your Round"
        accent="Faster?"
        subtitle="Start with your free DecX Score."
        ctaText="Get Free Score"
        ctaLink="/score"
      />
    </div>
  );
};

// For Sales Page - Exact original content
export const ForSales = () => {
  const { theme } = useTheme();

  const items = [
    'DecX Score — slide-by-slide effectiveness',
    'Buyer Insights — what they need to decide',
    'Competitor Benchmarks — how you stack up',
    'Pitch Canvas — structure that converts',
    'Follow-Up Sequences — emails that get replies',
    'Battle Cards — objection handling by competitor'
  ];

  const testimonials = [
    { quote: 'Close rate went from 12% to 28% in one quarter.', name: 'Mike R', role: 'Sales Lead' },
    { quote: "We stopped losing deals to 'we need to think about it.'", name: 'Jennifer K', role: 'VP Sales' },
    { quote: 'The battle cards are game-changing. Won 3x more competitive deals.', name: 'Tom H', role: 'AE Manager' },
    { quote: 'Follow-up sequences got us 45% more responses from prospects.', name: 'Lisa M', role: 'SDR Lead' },
    { quote: 'Buyer insights helped us tailor every pitch perfectly.', name: 'Chris P', role: 'Sales Director' },
    { quote: 'Finally understand why deals were stalling at slide 6.', name: 'Amanda B', role: 'Enterprise Sales' }
  ];

  return (
    <div data-testid="for-sales-page">
      <PageHero
        eyebrow="FOR SALES TEAMS"
        title="For Sales Teams Who Want"
        accent="to Close More"
        subtitle="Your sales deck is either helping you close — or killing deals quietly."
        cta="Get Free Score"
        ctaLink="/score"
      >
        <MockupGraphic type="chart" label="Sales Intelligence" />
      </PageHero>

      <Sec dark={theme === 'light'} style={{ paddingTop: '40px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <SH
            eyebrow="WHAT YOU GET"
            title="Everything to"
            accent="Close More Deals"
            center
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginTop: '32px' }}
          >
            <Crd glow>
              <BulletList items={items} />
            </Crd>
          </motion.div>
        </div>
      </Sec>

      <Sec dark={theme === 'light'} style={{ paddingTop: '0', overflow: 'hidden' }}>
        <TestimonialsSection
          eyebrow="TESTIMONIALS"
          title="Sales Teams Who"
          accent="Closed"
          testimonials={testimonials}
        />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginTop: '48px' }}
        >
          <p style={{ fontSize: '18px', fontWeight: 600, color: C.secondary }}>
            Join sales teams with 40%+ close rate improvement.
          </p>
        </motion.div>
      </Sec>

      <BottomCTA
        title="Ready to Close More"
        accent="Deals?"
        subtitle="Start with your free DecX Score."
        ctaText="Get Free Score"
        ctaLink="/score"
      />
    </div>
  );
};

// For PMs Page - Exact original content
export const ForPMs = () => {
  const { theme } = useTheme();

  const items = [
    'DecX Score — where executives lose interest',
    'Stakeholder Insights — what they need to approve',
    'ROI Framework — make the business case clear',
    'Pitch Canvas — structure that drives decisions',
    'Pre-Read Templates — prep stakeholders before meetings',
    "Strategy Calls — 30 mins with PMs who've won budgets"
  ];

  const testimonials = [
    { quote: 'Shortened my approval cycle from 3 months to 3 weeks.', name: 'David L', role: 'Sr. Product Manager' },
    { quote: 'Finally understood why leadership kept asking for more data.', name: 'Emily R', role: 'Product Lead' },
    { quote: 'The ROI framework made our business case bulletproof.', name: 'Jason K', role: 'PM Director' },
    { quote: 'Pre-read templates got stakeholders aligned before meetings.', name: 'Rachel T', role: 'Group PM' },
    { quote: 'Strategy call helped me reframe our initiative perfectly.', name: 'Mark S', role: 'Senior PM' },
    { quote: 'Stakeholder insights revealed exactly what CFO needed.', name: 'Nina P', role: 'Product Manager' }
  ];

  return (
    <div data-testid="for-pms-page">
      <PageHero
        eyebrow="FOR PRODUCT MANAGERS"
        title="For PMs Tired of"
        accent="'Needs More Data'"
        subtitle="Your product roadmap is solid. Your deck should make that obvious."
        cta="Get Free Score"
        ctaLink="/score"
        twoColumn
      >
        <MockupGraphic type="artefacts" label="Product Intelligence" />
      </PageHero>

      <Sec dark={theme === 'light'} style={{ paddingTop: '40px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <SH
            eyebrow="WHAT YOU GET"
            title="Everything to"
            accent="Win Approval"
            center
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginTop: '32px' }}
          >
            <Crd glow>
              <BulletList items={items} />
            </Crd>
          </motion.div>
        </div>
      </Sec>

      <Sec dark={theme === 'light'} style={{ paddingTop: '0', overflow: 'hidden' }}>
        <TestimonialsSection
          eyebrow="TESTIMONIALS"
          title="PMs Who Got"
          accent="Approved"
          testimonials={testimonials}
        />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginTop: '48px' }}
        >
          <p style={{ fontSize: '18px', fontWeight: 600, color: C.secondary }}>
            Join PMs who shortened approval cycles by 50%+.
          </p>
        </motion.div>
      </Sec>

      <BottomCTA
        title="Ready to Get"
        accent="Approved?"
        subtitle="Start with your free DecX Score."
        ctaText="Get Free Score"
        ctaLink="/score"
      />
    </div>
  );
};

// For Accelerators Page - Exact original content
export const ForAccelerators = () => {
  const { theme } = useTheme();

  const items = [
    'Bulk Deck Scoring — score entire cohorts',
    'Portfolio Benchmarking — compare companies',
    'White-Label Reports — your branding, our intelligence',
    'Cohort Analytics — track improvement over time',
    'Strategy Calls — for portfolio companies',
    'API Access — integrate with your systems'
  ];

  return (
    <div data-testid="for-accelerators-page">
      <PageHero
        eyebrow="FOR ACCELERATORS"
        title="Portfolio Companies"
        accent="Ready to Raise"
        subtitle="Your startups have potential. Their decks should prove it."
        cta="Contact for Enterprise"
        ctaLink="/contact"
        twoColumn
      >
        <MockupGraphic type="dashboard" label="Portfolio Dashboard" />
      </PageHero>

      <Sec dark={theme === 'light'} style={{ paddingTop: '40px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <SH
            eyebrow="ENTERPRISE FEATURES"
            title="Scale Your"
            accent="Program"
            center
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginTop: '32px' }}
          >
            <Crd glow>
              <BulletList items={items} />
            </Crd>
          </motion.div>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Scale Your"
        accent="Program?"
        subtitle="Let's discuss custom enterprise solutions."
        ctaText="Contact Sales"
        ctaLink="/contact"
      />
    </div>
  );
};

// Intelligence Sub-Pages - Exact original content
export const PitchIntel = () => {
  const { theme } = useTheme();

  const intelligence = [
    'DecX Score across 5 dimensions',
    "Unexplored gaps you didn't know existed",
    'Investor persona insights',
    'Industry trend benchmarks'
  ];

  const artefacts = [
    'Pitch Canvas to structure your narrative',
    'AI prompts to rebuild weak slides',
    'Investor outreach sequences',
    'Objection battle cards'
  ];

  return (
    <div data-testid="pitch-intel-page">
      <PageHero
        eyebrow="PITCH DECK INTELLIGENCE"
        title="Pitch Deck Intelligence"
        accent="for Founders Who Want the Yes"
        subtitle="Your pitch deck isn't just slides. It's the gatekeeper to your round."
        body="DecX AI transforms your deck from interesting to let's do this."
        cta="Get Free Score"
        ctaLink="/score"
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px' }}>
            <Crd>
              <h3 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '20px' }}>INTELLIGENCE</h3>
              <BulletList items={intelligence} />
            </Crd>
            <Crd>
              <h3 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '20px' }}>ARTEFACTS</h3>
              <BulletList items={artefacts} />
            </Crd>
          </div>

          <Crd glow style={{ marginTop: '32px', textAlign: 'center' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '12px' }}>STRATEGY</h3>
            <p className="font-[Manrope]" style={{ fontSize: '18px', marginBottom: '20px' }}>
              1:1 calls with operators who've raised $250M+
            </p>
            <p style={{ fontSize: '16px', fontWeight: 600, color: C.secondary }}>
              Trusted by founders who've raised $500M+.
            </p>
          </Crd>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Transform Your Deck"
        accent="to DecX?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free Score"
        ctaLink="/score"
      />
    </div>
  );
};

export const SalesIntel = () => {
  const { theme } = useTheme();

  const intelligence = [
    'DecX Score across 5 dimensions',
    'Slide-by-slide effectiveness analysis',
    'Buyer persona mapping',
    'Competitor positioning gaps'
  ];

  const artefacts = [
    'Pitch Canvas for your sales narrative',
    'AI prompts to sharpen weak slides',
    'Follow-up email sequences',
    'Objection battle cards'
  ];

  return (
    <div data-testid="sales-intel-page">
      <PageHero
        eyebrow="SALES DECK INTELLIGENCE"
        title="Sales Deck Intelligence"
        accent="for Teams Who Want to Close"
        subtitle="Your sales deck gets 10 minutes. Most lose the deal by slide 5."
        body="DecX AI transforms your deck from 'we'll get back to you' to signed contract."
        cta="Get Free Score"
        ctaLink="/score"
      >
        <MockupGraphic type="chart" label="Sales Intelligence" />
      </PageHero>

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px' }}>
            <Crd>
              <h3 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '20px' }}>INTELLIGENCE</h3>
              <BulletList items={intelligence} />
            </Crd>
            <Crd>
              <h3 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '20px' }}>ARTEFACTS</h3>
              <BulletList items={artefacts} />
            </Crd>
          </div>

          <Crd glow style={{ marginTop: '32px', textAlign: 'center' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '12px' }}>STRATEGY</h3>
            <p className="font-[Manrope]" style={{ fontSize: '18px', marginBottom: '20px' }}>
              1:1 calls with sales leaders who've closed
            </p>
            <p style={{ fontSize: '16px', fontWeight: 600, color: C.secondary }}>
              Sales teams: 40%+ improvement in close rates.
            </p>
          </Crd>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Close More"
        accent="Deals?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free Score"
        ctaLink="/score"
      />
    </div>
  );
};

export const ProductIntel = () => {
  const { theme } = useTheme();

  const intelligence = [
    'DecX Score across 5 dimensions',
    'Executive attention mapping',
    'Decision-maker persona insights',
    'ROI narrative gaps'
  ];

  const artefacts = [
    'Pitch Canvas for internal presentations',
    'AI prompts to sharpen the narrative',
    'Pre-read document templates',
    'Stakeholder alignment guides'
  ];

  return (
    <div data-testid="product-intel-page">
      <PageHero
        eyebrow="PRODUCT DECK INTELLIGENCE"
        title="Product Deck Intelligence"
        accent="for PMs Who Need Buy-In"
        subtitle="Getting stakeholder approval shouldn't take 6 meetings."
        body="DecX AI transforms your deck from 'needs more data' to approved."
        cta="Get Free Score"
        ctaLink="/score"
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px' }}>
            <Crd>
              <h3 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '20px' }}>INTELLIGENCE</h3>
              <BulletList items={intelligence} />
            </Crd>
            <Crd>
              <h3 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '20px' }}>ARTEFACTS</h3>
              <BulletList items={artefacts} />
            </Crd>
          </div>

          <Crd glow style={{ marginTop: '32px', textAlign: 'center' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '12px' }}>STRATEGY</h3>
            <p className="font-[Manrope]" style={{ fontSize: '18px', marginBottom: '20px' }}>
              1:1 calls with product leaders who've won budgets
            </p>
            <p style={{ fontSize: '16px', fontWeight: 600, color: C.secondary }}>
              PMs shortened approval cycles by 50%+.
            </p>
          </Crd>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Get"
        accent="Approved?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free Score"
        ctaLink="/score"
      />
    </div>
  );
};

// Outcome Pages - Exact original content
export const CloseRound = () => {
  const { theme } = useTheme();

  const sections = [
    { title: 'KNOW WHERE YOU STAND', items: ['DecX Score in 24 hours', '5-dimension breakdown', 'Top issues killing your raise'] },
    { title: 'KNOW YOUR AUDIENCE', items: ['Investor persona mapping', 'What they need to see', 'Red flags that trigger no'] },
    { title: 'GET READY TO CLOSE', items: ['Pitch Canvas', 'Deck rebuild prompts', 'Investor outreach sequences'] },
    { title: 'TALK TO AN EXPERT', items: ['30-min strategy calls', "Operators who've raised $250M+"] }
  ];

  return (
    <div data-testid="close-round-page">
      <PageHero
        eyebrow="CLOSE YOUR ROUND"
        title="Close Your Round."
        accent="Faster."
        subtitle="87 decks sent. 11 meetings. Zero term sheets. The problem isn't your startup. It's your deck."
        cta="Get Free Score"
        ctaLink="/score"
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px' }}>
            {sections.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Crd>
                  <h4 style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '16px' }}>
                    {s.title}
                  </h4>
                  <BulletList items={s.items} small />
                </Crd>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginTop: '48px' }}
          >
            <p style={{ fontSize: '18px', fontWeight: 600, color: C.secondary }}>
              Founders using DecX AI have raised $500M+.
            </p>
          </motion.div>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Close Your Round"
        accent="Faster?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free Score"
        ctaLink="/score"
      />
    </div>
  );
};

export const CloseDeals = () => {
  const { theme } = useTheme();

  const sections = [
    { title: 'KNOW WHAT WORKS', items: ['DecX Score across 5 dimensions', 'Slide-by-slide effectiveness', 'Where buyers drop off'] },
    { title: 'KNOW YOUR BUYER', items: ['Buyer persona mapping', 'What they need to decide', 'Objections before they come up'] },
    { title: 'CLOSE FASTER', items: ['Pitch Canvas for sales', 'Follow-up sequences', 'Battle cards by competitor'] }
  ];

  return (
    <div data-testid="close-deals-page">
      <PageHero
        eyebrow="CLOSE MORE DEALS"
        title="Close More Deals."
        accent="Lose Fewer to Bad Decks."
        subtitle="40% of lost deals trace back to the deck. Not the product. Not the pricing. The deck."
        cta="Get Free Score"
        ctaLink="/score"
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {sections.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Crd>
                  <h4 style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '16px' }}>
                    {s.title}
                  </h4>
                  <BulletList items={s.items} small />
                </Crd>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginTop: '48px' }}
          >
            <p style={{ fontSize: '18px', fontWeight: 600, color: C.secondary }}>
              Sales teams improved close rates by 40%+.
            </p>
          </motion.div>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Close More"
        accent="Deals?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free Score"
        ctaLink="/score"
      />
    </div>
  );
};

export const WinApproval = () => {
  const { theme } = useTheme();

  const sections = [
    { title: 'KNOW WHERE YOU STAND', items: ['DecX Score across 5 dimensions', 'Executive attention analysis', 'Where decision-makers lose interest'] },
    { title: 'KNOW STAKEHOLDERS', items: ['Executive persona mapping', 'What they need to say yes', 'Budget objection patterns'] },
    { title: 'GET APPROVED', items: ['Pitch Canvas', 'ROI framework templates', 'Decision-enabling structure'] }
  ];

  return (
    <div data-testid="win-approval-page">
      <PageHero
        eyebrow="WIN APPROVAL"
        title="Win Stakeholder Approval."
        accent="Without 12 Revisions."
        subtitle="'Looks good, but we need more data.' Sound familiar? DecX AI transforms your deck from loops to approved."
        cta="Get Free Score"
        ctaLink="/score"
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {sections.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Crd>
                  <h4 style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '16px' }}>
                    {s.title}
                  </h4>
                  <BulletList items={s.items} small />
                </Crd>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginTop: '48px' }}
          >
            <p style={{ fontSize: '18px', fontWeight: 600, color: C.secondary }}>
              PMs shortened approval cycles by 50%+.
            </p>
          </motion.div>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Get"
        accent="Approved?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free Score"
        ctaLink="/score"
      />
    </div>
  );
};
